import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';
import { authService, userService } from '../services/firebase';
import { User } from '../types';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  loading: boolean;
  signInWithPhone: (phoneNumber: string, appVerifier: any) => Promise<any>;
  verifyPhoneCode: (confirmationResult: any, code: string) => Promise<FirebaseUser>;
  signOut: () => Promise<void>;
  updateUserProfile: (data: Partial<User>) => Promise<void>;
  registerWithEmail: (name: string, email: string, password: string) => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<FirebaseUser>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          const profile = await userService.getUser(user.uid);
          setUserProfile(profile);
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signInWithPhone = async (phoneNumber: string, appVerifier: any) => {
    return await authService.signInWithPhone(phoneNumber, appVerifier);
  };

  const verifyPhoneCode = async (confirmationResult: any, code: string) => {
    return await authService.verifyPhoneCode(confirmationResult, code);
  };

  const signOut = async () => {
    await authService.signOut();
    setCurrentUser(null);
    setUserProfile(null);
  };

  const updateUserProfile = async (data: Partial<User>) => {
    if (!currentUser) {
      throw new Error('Kullanıcı giriş yapmamış');
    }

    try {
      console.log('Updating user profile for:', currentUser.uid);
      console.log('Update data:', data);
      
      // Önce kullanıcının var olup olmadığını kontrol et
      let existingProfile = await userService.getUser(currentUser.uid);
      
      if (!existingProfile) {
        console.log('User profile not found, creating new profile');
        // Kullanıcı profili yoksa oluştur
        const newUserData = {
          displayName: data.displayName || currentUser.displayName || '',
          email: data.email || currentUser.email || '',
          phoneNumber: data.phoneNumber || currentUser.phoneNumber || '',
          role: 'user' as const,
          bio: data.bio || '',
          avatar: '',
          isVerified: false,
          lastLoginAt: Timestamp.fromDate(new Date())
        };
        
        await userService.createUserWithUID(currentUser.uid, newUserData);
        console.log('New user profile created with UID:', currentUser.uid);
        
        // Yeni profili al
        existingProfile = await userService.getUser(currentUser.uid);
        setUserProfile(existingProfile);
      } else {
        console.log('User profile found, updating existing profile');
        // Mevcut profili güncelle
        await userService.updateUser(currentUser.uid, data);
        
        // Güncellenmiş profili al
        const updatedProfile = await userService.getUser(currentUser.uid);
        setUserProfile(updatedProfile);
      }
      
      console.log('Profile update completed successfully');
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const registerWithEmail = async (name: string, email: string, password: string) => {
    // Firebase ile e-posta/şifre kaydı
    const userCredential = await authService.registerWithEmail(email, password);
    const user = userCredential.user;
    // Profil güncelle
    await (user as any).updateProfile({ displayName: name });
    // Email verification gönder
    await (user as any).sendEmailVerification();
    // Firestore'da kullanıcı profilini oluştur
    await userService.createUserWithUID(user.uid, {
      displayName: name,
      email: user.email || email,
      phoneNumber: user.phoneNumber || '',
      role: 'user',
      bio: '',
      avatar: '',
      isVerified: false,
      lastLoginAt: Timestamp.fromDate(new Date())
    });
    // Kullanıcıyı çıkış yaptır
    await authService.signOut();
  };

  const loginWithEmail = async (email: string, password: string) => {
    const userCredential = await authService.loginWithEmail(email, password);
    return userCredential.user;
  };

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    signInWithPhone,
    verifyPhoneCode,
    signOut,
    updateUserProfile,
    registerWithEmail,
    loginWithEmail
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 